import React, { useState } from 'react';
import { Alert, Button, Card, Collapse, Spinner } from 'react-bootstrap';
import { Upload, message } from 'antd';
import {
  UploadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  FileOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { flaskServiceAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';

const MhtmlInstructions = () => (
  <div style={{ fontSize: '13px', lineHeight: 1.6 }}>
    <p className="mb-2">
      Откройте нужную страницу в браузере, дождитесь полной загрузки и сохраните её как MHTML.
      Затем загрузите полученный файл сюда — он заменит автоматически сохранённую копию в протоколе.
    </p>
    <strong>Google Chrome / Microsoft Edge / Яндекс.Браузер</strong>
    <ol className="mb-2 ps-3">
      <li>Откройте страницу по тому же URL, что указан в задаче</li>
      <li>Нажмите <kbd>Ctrl</kbd>+<kbd>S</kbd> (или меню → «Сохранить как…»)</li>
      <li>Тип файла: <strong>«Веб-страница, один файл»</strong> (*.mhtml)</li>
      <li>Сохраните файл и загрузите его ниже</li>
    </ol>
    <strong>Mozilla Firefox</strong>
    <ol className="mb-2 ps-3">
      <li>Откройте страницу по тому же URL</li>
      <li>Нажмите <kbd>Ctrl</kbd>+<kbd>S</kbd></li>
      <li>Тип: <strong>«Веб-страница, полностью»</strong> или MHTML (если доступно)</li>
      <li>Сохраните и загрузите файл</li>
    </ol>
    <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
      Принимаются файлы .mhtml и .mht (от 1 КБ). Убедитесь, что на странице виден нужный контент
      до сохранения — авторизация и динамический контент должны быть уже загружены.
    </p>
  </div>
);

const MhtmlUploadPanel = ({
  taskId,
  hasWarnings = false,
  onSuccess,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || hasWarnings);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const isValidMhtmlFile = (file) => {
    const name = (file.name || '').toLowerCase();
    return name.endsWith('.mhtml') || name.endsWith('.mht');
  };

  const handleFileSelect = (file) => {
    if (!isValidMhtmlFile(file)) {
      message.warning('Выберите файл с расширением .mhtml или .mht');
      return false;
    }
    if (file.size < 1024) {
      message.warning('Файл слишком маленький (минимум 1 КБ)');
      return false;
    }
    setSelectedFile(file);
    setUploadError(null);
    setUploadSuccess(false);
    return false;
  };

  const handleUpload = async () => {
    if (!selectedFile || !taskId) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const response = await flaskServiceAPI.replaceMhtml(taskId, selectedFile);
      setUploadSuccess(true);
      setSelectedFile(null);
      message.success('Страница MHTML успешно загружена');
      onSuccess?.(response.task || response);
    } catch (err) {
      const errorText = getTranslatedError(
        err.response?.data?.error || err.message,
        'Не удалось загрузить MHTML'
      );
      setUploadError(errorText);
      message.error(errorText);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  return (
    <Card className="border-0 mt-3" style={{ background: hasWarnings ? 'rgba(255, 193, 7, 0.08)' : 'rgba(0, 0, 0, 0.02)', borderRadius: '10px' }}>
      <Card.Body className="p-3">
        {hasWarnings && (
          <Alert variant="warning" className="py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
            <InfoCircleOutlined style={{ marginRight: '6px' }} />
            <strong>Рекомендуем загрузить страницу вручную.</strong>{' '}
            Автоматически сохранённая копия может быть неполной. Откройте страницу в браузере,
            сохраните как MHTML и загрузите файл — предупреждения исчезнут, если файл корректный.
          </Alert>
        )}

        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2"
          onClick={() => setExpanded(!expanded)}
          style={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f' }}
        >
          <UploadOutlined />
          {hasWarnings ? 'Загрузить свою копию страницы (MHTML)' : 'Заменить страницу своим MHTML-файлом'}
          {expanded ? <UpOutlined style={{ fontSize: '12px' }} /> : <DownOutlined style={{ fontSize: '12px' }} />}
        </button>

        <Collapse in={expanded}>
          <div className="mt-3">
            <Alert variant="info" className="py-2 px-3 mb-3 border-0" style={{ fontSize: '13px', background: 'rgba(24, 144, 255, 0.08)' }}>
              <InfoCircleOutlined style={{ marginRight: '6px' }} />
              <strong>Как сохранить страницу</strong>
            </Alert>
            <MhtmlInstructions />

            <div className="mt-3">
              {uploadSuccess && (
                <Alert variant="success" className="py-2 px-3 mb-2" style={{ fontSize: '13px' }}>
                  <CheckCircleOutlined style={{ marginRight: '6px' }} />
                  Файл загружен. Страница в протоколе обновлена.
                </Alert>
              )}

              {uploadError && (
                <Alert variant="danger" className="py-2 px-3 mb-2" style={{ fontSize: '13px' }}>
                  {uploadError}
                </Alert>
              )}

              {selectedFile ? (
                <div
                  className="d-flex align-items-center justify-content-between p-2 mb-2"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <FileOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500 }}>{selectedFile.name}</div>
                      <div className="text-muted" style={{ fontSize: '11px' }}>
                        {formatFileSize(selectedFile.size)}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                    disabled={uploading}
                  >
                    <DeleteOutlined />
                  </Button>
                </div>
              ) : (
                <Upload
                  accept=".mhtml,.mht"
                  showUploadList={false}
                  beforeUpload={handleFileSelect}
                  disabled={uploading}
                >
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                    disabled={uploading}
                  >
                    <UploadOutlined /> Выбрать файл .mhtml
                  </button>
                </Upload>
              )}

              {selectedFile && (
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-2 d-flex align-items-center gap-2"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Загрузка...
                    </>
                  ) : (
                    <>
                      <UploadOutlined />
                      Загрузить в протокол
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

export default MhtmlUploadPanel;
